import { observer } from 'mobx-react';
import React, { ReactNode, useCallback } from 'react';

import { Button, CircularProgress, Grid, Typography } from '@mui/material';
import Form from 'components/Form';
import MediaTypography from 'components/MediaTypography';
import { FormikProps, FormikValues } from 'formik';
import { ObjectSchema } from 'yup';

import { StyledHeaderWrap, StyledLayoutWrap } from './styled';

interface ILayoutProps<T extends FormikValues = FormikValues> {
	header: string;
	headerText: string;
	children?: ReactNode;
	footer?: ReactNode;
	buttonText?: string;
	initialValues?: T;
	onSubmit?: (data: T, ...rest: any) => void;
	validationSchema: ObjectSchema<T>;
}

function Layout<T extends FormikValues = FormikValues>({
	header,
	headerText,
	children,
	footer,
	buttonText,
	initialValues = {} as T,
	onSubmit,
	validationSchema,
}: ILayoutProps<T>) {
	const renderFooter = useCallback(
		({ isSubmitting }: FormikProps<T>) => (
			<Grid container gap="24px" direction="column">
				<Grid item>
					<Button
						type="submit"
						fullWidth
						variant="contained"
						startIcon={isSubmitting ? <CircularProgress color="inherit" size={15} /> : undefined}
						disabled={isSubmitting}
					>
						{buttonText}
					</Button>
				</Grid>
				<Grid item>{footer}</Grid>
			</Grid>
		),
		[buttonText, footer],
	);

	const handleSubmit = async (values: T) => {
		await onSubmit(values);
	};

	return (
		<StyledLayoutWrap elevation={5}>
			<Form
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSave={handleSubmit}
				hideActions
				footer={renderFooter}
			>
				<Grid container direction="column" gap="24px">
					<StyledHeaderWrap container>
						<MediaTypography variant="headLine" align="center" color="text.headLine">
							{header}
						</MediaTypography>
						<Typography variant="body1" lineHeight="22px" align="center" color="text.md500">
							{headerText}
						</Typography>
					</StyledHeaderWrap>
					<Grid container gap="24px" direction="column">
						{children}
					</Grid>
				</Grid>
			</Form>
		</StyledLayoutWrap>
	);
}

export default observer(Layout);
